/**
 * Date Formatting Utilities
 *
 * Pure functions for consistent date formatting across the application.
 * Handles API date formats, display formats, and Norwegian locale formatting.
 */

import { format } from "date-fns";
import { nb } from "date-fns/locale";

/**
 * Format date for API requests (yyyy-MM-dd)
 * Used for from_date, to_date parameters
 * @param date Date to format
 * @returns ISO date string (yyyy-MM-dd)
 */
export function formatAPIDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * Format date for display (dd.MM.yyyy)
 * Norwegian date format used throughout the UI
 * @param date Date to format
 * @returns Formatted date string (dd.MM.yyyy)
 */
export function formatDisplayDate(date: Date): string {
  return format(date, "dd.MM.yyyy");
}

/**
 * Format date as unique key for grouping/mapping
 * Consistent with toISOString().split('T')[0]
 * @param date Date to format
 * @returns Date key string (yyyy-MM-dd)
 */
export function formatDateKey(date: Date): string {
  return date.toISOString().split("T")[0];
}

/**
 * Format date in short format (d.MM)
 * Used for compact date displays like "24.11"
 * @param date Date to format
 * @returns Short date string (d.MM)
 */
export function formatShortDate(date: Date): string {
  return format(date, "d.MM");
}

/**
 * Format month and year (MMMM yyyy)
 * Norwegian locale, e.g., "januar 2024"
 * @param date Date to format
 * @returns Month and year string in Norwegian
 */
export function formatMonthYear(date: Date): string {
  return format(date, "MMMM yyyy", { locale: nb });
}

/**
 * Format full weekday name (EEEE)
 * Norwegian locale, e.g., "mandag"
 * @param date Date to format
 * @returns Full weekday name in Norwegian
 */
export function formatWeekday(date: Date): string {
  return format(date, "EEEE", { locale: nb });
}

/**
 * Format short weekday name (EEE)
 * Norwegian locale, e.g., "man."
 * @param date Date to format
 * @returns Short weekday name in Norwegian
 */
export function formatShortWeekday(date: Date): string {
  return format(date, "EEE", { locale: nb });
}

/**
 * Format full month name (MMMM)
 * Norwegian locale, e.g., "januar"
 * @param date Date to format
 * @returns Full month name in Norwegian
 */
export function formatMonthName(date: Date): string {
  return format(date, "MMMM", { locale: nb });
}

/**
 * Format short month name (MMM)
 * Norwegian locale, e.g., "jan."
 * @param date Date to format
 * @returns Short month name in Norwegian
 */
export function formatShortMonthName(date: Date): string {
  return format(date, "MMM", { locale: nb });
}

/**
 * Format hours for display (X T)
 * Rounds to 1 decimal place to avoid floating-point artifacts like "76.44999999999999"
 * Used in conflict resolution and summary displays
 * @param hours Number of hours
 * @returns Formatted hours string with T suffix
 */
export function formatHours(hours: number): string {
  // Round to 1 decimal place to avoid floating-point display issues
  const rounded = Math.round(hours * 10) / 10;
  // Display as integer if no decimals, otherwise show 1 decimal
  const display = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1);
  return `${display} T`;
}

/**
 * Format booking hours (X time/timer)
 * Rounds to 1 decimal place to avoid floating-point artifacts like "76.44999999999999"
 * Single hour: "1 time", multiple hours: "8 timer"
 * Used in booking cards and booking displays
 * @param hours Number of hours
 * @returns Formatted booking hours string in Norwegian
 */
export function formatBookingHours(hours: number): string {
  // Round to 1 decimal place to avoid floating-point display issues
  const rounded = Math.round(hours * 10) / 10;
  const display = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1);
  return rounded === 1 ? "1 time" : `${display} timer`;
}

/**
 * Format date in long format (d. MMMM yyyy)
 * Norwegian locale, e.g., "3. desember 2025"
 * Used in booking detail sidebar
 * @param date Date to format
 * @returns Long date string in Norwegian
 */
export function formatLongDate(date: Date): string {
  return format(date, "d. MMMM yyyy", { locale: nb });
}

/**
 * Format datetime for display (dd.MM.yyyy, HH:mm)
 * Norwegian format used for timestamps, e.g., "03.12.2025, 14:30"
 * @param date Date to format
 * @returns Formatted datetime string
 */
export function formatDateTime(date: Date): string {
  return format(date, "dd.MM.yyyy, HH:mm");
}

/**
 * Format date with weekday and month (EEEE d. MMMM)
 * Norwegian locale, e.g., "mandag 6. januar"
 * Used in modal titles for booking editing
 * @param date Date to format
 * @returns Weekday and date string in Norwegian
 */
export function formatWeekdayDate(date: Date): string {
  return format(date, "EEEE d. MMMM", { locale: nb });
}
